// useHistorialLogic.ts (SIN JSX)
import { useCallback, useState } from "react";
import { Button, Popconfirm } from "antd";
import { getMsg } from "../../utils/antdStatic";
import React from "react";
import { API_BASE_URL, getCsrfToken } from "../../services/apiClient";
import { useSidebar } from "../../hooks/useSidebar";

type UseHistorialLogicReturn = {
  // devuelve un React element, pero sin escribir JSX aquí
  undoButton: (historyId: string) => React.ReactElement;
  undoRecord: (historyId: string) => Promise<boolean>;
  undoingId: string | null;
  toggleSidebar: () => void;
  sidebarOpen: boolean;
};

export function useHistorialLogic(
  onAfterUndo?: () => void
): UseHistorialLogicReturn {
  const [undoingId, setUndoingId] = useState<string | null>(null);
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const undoRecord = useCallback(
    async (historyId: string) => {
      try {
        setUndoingId(historyId);
        // Usamos fetch directo (no `apiFetch`) porque distinguimos 409 del
        // resto para mostrar un mensaje específico ("estado cambió"). El
        // interceptor global agrega `credentials: "include"` (cookie auth)
        // y captura 401 → forceLogout. CSRF se manda manual: en mutaciones
        // el backend lo exige cuando la sesión es por cookie HttpOnly.
        // Round 2026-04-26 fase 6 (P1-B).
        const csrf = getCsrfToken();
        const resp = await fetch(
          `${API_BASE_URL}/historial/${historyId}/undo`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              ...(csrf ? { "X-CSRF-Token": csrf } : {}),
            },
          }
        );
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          getMsg()?.warning(
            resp.status === 409
              ? "No se puede deshacer: el estado cambió."
              : json?.message || json?.error || "No se pudo deshacer"
          );
          return false;
        }
        getMsg()?.success(json?.mensaje || "Acción deshecha");
        onAfterUndo?.();
        return true;
      } catch (e: any) {
        getMsg()?.error(e?.message || "No se pudo deshacer");
        return false;
      } finally {
        setUndoingId(null);
      }
    },
    [onAfterUndo]
  );

  const undoButton = useCallback(
    (historyId: string) =>
      React.createElement(
        Popconfirm,
        {
          title: "¿Deshacer esta acción?",
          description: "Esta operación revertirá los cambios realizados.",
          okText: "Deshacer",
          okType: "danger" as const,
          cancelText: "Cancelar",
          onConfirm: () => undoRecord(historyId),
        },
        React.createElement(
          Button,
          {
            type: "link",
            danger: true,
            loading: undoingId === historyId,
            disabled: !!undoingId && undoingId !== historyId,
          },
          "Deshacer"
        )
      ),
    [undoingId, undoRecord]
  );

  return { undoButton, undoRecord, toggleSidebar, sidebarOpen, undoingId };
}
